import {Pin} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionIcon from "@mui/icons-material/Description";
import ReplyIcon from '@mui/icons-material/Reply';
import SecurityIcon from '@mui/icons-material/Security';
import {GridColDef, GridRowParams, GridValueFormatterParams} from "@mui/x-data-grid";
import {GridValueGetterParams} from "@mui/x-data-grid/models/params/gridCellParams";
import React, {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import userService from "../../../../common/apis/user/UserApiClient";
import {UserDto} from "../../../../common/apis/user/UserDto";
import {DateTimeFormatter} from "../../../../common/DateTimeFormatter";
import AlertDialog, {AlertDialogType} from "../../../../components/dialogs/AlertDialog";
import ExtGridWrapper from "../../../../components/grid/ExtGridWrapper";
import {ActionButtonType, EnabledPolicy, GridAction} from "../../../../components/grid/GridAction";
import StackedIcon, {Position} from "../../../../components/StackedIcon";
import {PersistentStateId} from "../../../../store/common/Grid";
import {_transl} from "../../../../store/localization/TranslMessasge";
import {IApplicationState} from "../../../../store/Store";
import {CommonTranslationKey} from "../CommonTranslationKey";
import {ConfigurationId} from "../import/Configuration";
import ImportDialog from "../import/ImportDialog";
import {ButtonId} from "./ButtonId";
import constructUsersController, {PermissionsData, UsersPageController} from "./controller/UsersPageController";
import {UserRoleTranslator} from "./role/UserRoleTranslator";
import UserActivationDialog from "./UserActivationDialog";
import UserCreateDialog from "./UserCreateDialog";
import UserDeactivationDialog from "./UserDeactivationDialog";
import UserDetailDialog from "./UserDetailDialog";
import UserFormatter from "./UserFormatter";
import {UserPasswordChangeDialog} from "./UserPasswordChangeDialog";
import {UsersTranslationKey} from "./UsersTranslationKey";

const userActiveFormatter = (params: GridValueFormatterParams) => params.value === true ?
    _transl(CommonTranslationKey.YES) : _transl(CommonTranslationKey.NO);
const roleFormatter = (params: GridValueFormatterParams) => UserRoleTranslator.get().translateUserRole(params.value as string) ?? "";

const fullNameGetter = (params: GridValueGetterParams) => {
    return UserFormatter.formatFullName(params.row.firstName, params.row.lastName);
}

type MountStatus = {
    mounted: boolean,
}

type AlertDialogInfo = {
    type: AlertDialogType,
    title: string,
    text: string,
}

type ImportDialogInfo = {
    configurationId: ConfigurationId,
}

export default function UsersGrid() {

    const [refreshUsersRequest, setRefreshUsersRequest] = useState<number>(1);
    const [users, setUsers] = useState<UserDto[]>([]);
    const [selectedLogin, setSelectedLogin] = useState<string | undefined>();
    const [activationDialogOpen, setActivationDialogOpen] = useState<boolean>(false);
    const [deactivationDialogOpen, setDeactivationDialogOpen] = useState<boolean>(false);
    const [changeUserPasswordDialogIsOpen, setChangeUserPasswordDialogIsOpen] = useState<boolean>(false);
    const [userCreateDialogIsOpen, setUserCreateDialogIsOpen] = useState<boolean>(false);
    const [userDetailDialogIsOpen, setUserDetailDialogIsOpen] = useState<boolean>(false);
    const [alertDialog, setAlertDialog] = useState<AlertDialogInfo>();
    const [importDialog, setImportDialog] = useState<ImportDialogInfo>();

    const loggedUser = useSelector((state: IApplicationState) => state.user.userData as UserDto);

    const controller = useRef<UsersPageController>(constructUsersController(userService))

    useEffect(() => {
        const mountStatus: MountStatus = {mounted: true};

        (async () => {
            const users = await controller.current.getAllUsers();
            if (mountStatus.mounted) {
                setUsers(users);
            }
        })();
        return () => {
            mountStatus.mounted = false;
        }
    }, [refreshUsersRequest]);


    function refreshUsers() {
        setRefreshUsersRequest(refreshUsersRequest + 1);
    }

    function onGridRowClicked(param: GridRowParams) {
        setSelectedLogin(param.row.login);
    }

    function openActivationDialog() {
        if (selectedUser) {
            setActivationDialogOpen(true);
        }
    }

    function closeActivationDialog(activated: boolean) {
        if (activated) {
            refreshUsers();
        }
        setActivationDialogOpen(false);
    }

    function openDeactivationDialog() {
        if (selectedUser) {
            setDeactivationDialogOpen(true);
        }
    }

    function closeDeactivationDialog(deactivated: boolean) {
        if (deactivated) {
            refreshUsers();
        }
        setDeactivationDialogOpen(false);
    }

    function getSelectedUser(): UserDto | undefined {
        if (selectedLogin) {
            return users.filter((user) => user.login === selectedLogin)[0];
        } else {
            return undefined;
        }
    }

    function showAlertDialog(type: AlertDialogType, title: string, text: string) {
        setAlertDialog({
            type: type,
            title: title,
            text: text,
        });
    }

    function closeUserDetailDialog(userUpdated: boolean) {
        setUserDetailDialogIsOpen(false);
        if (userUpdated) {
            refreshUsers();
        }
    }

    function closeCreateUserDialog(userCreated: boolean) {
        setUserCreateDialogIsOpen(false);
        if (userCreated) {
            refreshUsers();
        }
    }

    async function exportPermissions() {
        try {
            const data = await controller.current.exportPermissions();
            onPermissionsFetched(data);
        } catch (error) {
            onPermissionsFetchFailed();
        }
    }

    function onPermissionsFetched(data: PermissionsData) {
        controller.current.saveBlobData({blobParts: [data.data], fileName: data.fileName});
    }

    function onPermissionsFetchFailed() {
        showAlertDialog(AlertDialogType.ERROR, _transl(UsersTranslationKey.EXPORT_PERMISSIONS), _transl(UsersTranslationKey.EXPORT_PERMISSIONS_FAILED));
    }

    function hideImportDialog() {
        setImportDialog(undefined);
        refreshUsers();
    }

    function createColumns() {
        const gridColDefs: GridColDef<UserDto>[] = [
            {field: 'login', headerName: _transl(UsersTranslationKey.USER_LOGIN_NAME), width: 200},
            {
                field: 'fullName',
                headerName: _transl(UsersTranslationKey.USER_FULL_NAME),
                width: 200,
                valueGetter: fullNameGetter
            },
            {
                field: 'role',
                headerName: _transl(UsersTranslationKey.USER_ROLE),
                width: 200,
                valueFormatter: roleFormatter
            },
            {
                field: 'userActive',
                headerName: _transl(UsersTranslationKey.USER_IS_ACTIVE),
                width: 150,
                valueFormatter: userActiveFormatter
            },
            {field: 'email', headerName: _transl(UsersTranslationKey.USER_EMAIL), width: 200},
            {field: 'phone', headerName: _transl(UsersTranslationKey.USER_PHONE), width: 200},
            {
                field: 'createdAt',
                headerName: _transl(UsersTranslationKey.USER_CREATED),
                width: 200,
                valueFormatter: ({value}) => DateTimeFormatter.formatDateTime(value)
            },
            {
                field: 'lastLogin',
                headerName: _transl(UsersTranslationKey.USER_LAST_LOGIN),
                width: 200,
                valueFormatter: ({value}) => DateTimeFormatter.formatDateTime(value)
            },
        ];
        return gridColDefs;
    }

    const selectedUser = getSelectedUser();

    return (
        <React.Fragment>
            {alertDialog && <AlertDialog open={true}
                                         type={alertDialog.type}
                                         title={alertDialog.title}
                                         text={alertDialog.text}
                                         onClose={() => setAlertDialog(undefined)}/>
            }
            {importDialog && <ImportDialog isOpened={true}
                                           initialConfigurationId={importDialog.configurationId}
                                           onDialogClosed={hideImportDialog}/>
            }
            {selectedUser &&
                <UserDetailDialog opened={userDetailDialogIsOpen}
                                  onClose={(updated) => closeUserDetailDialog(updated)}
                                  user={selectedUser}
                />
            }
            <UserCreateDialog opened={userCreateDialogIsOpen}
                              onClose={(created) => closeCreateUserDialog(created)}
                              loggedUser={loggedUser}
                              controller={controller.current}
            />
            <UserActivationDialog open={activationDialogOpen}
                    user={selectedUser}
                    onClose={(activated) => closeActivationDialog(activated)}/>
            <UserDeactivationDialog open={deactivationDialogOpen}
                    user={selectedUser}
                    onClose={(activated) => closeDeactivationDialog(activated)}/>
            {selectedUser &&
                <UserPasswordChangeDialog open={changeUserPasswordDialogIsOpen}
                                          onClose={() => setChangeUserPasswordDialogIsOpen(false)}
                                          userLogin={selectedUser.login}
                />
            }
            <ExtGridWrapper
                columns={createColumns()}
                rows={users}
                rowCount={users.length}
                getRowId={row => row.login}
                actions={[
                    GridAction.buttonBuilder(ButtonId.USER_DETAIL, ActionButtonType.IMMEDIATE, _transl(UsersTranslationKey.USER),
                        <DescriptionIcon/>)
                        .enabledPolicy(EnabledPolicy.WHEN_EXACTLY_ONE_SELECTED)
                        .onClick(() => setUserDetailDialogIsOpen(true))
                        .build(),
                    GridAction.buttonBuilder(ButtonId.ADD_USER, ActionButtonType.IMMEDIATE, _transl(UsersTranslationKey.CREATE_USER),
                        <AddIcon/>)
                        .enabledPolicy(EnabledPolicy.ALWAYS)
                        .onClick(() => setUserCreateDialogIsOpen(true))
                        .isEnabled(() => controller.current.canCreateUsers(loggedUser.userAcl))
                        .build(),
                    GridAction.buttonBuilder(ButtonId.ACTIVATE_USER, ActionButtonType.IMMEDIATE, _transl(UsersTranslationKey.ACTIVATE_USER),
                        <CheckCircleOutlineIcon/>)
                        .enabledPolicy(EnabledPolicy.WHEN_EXACTLY_ONE_SELECTED)
                        .onClick(openActivationDialog)
                        .isEnabled(() => selectedUser ? controller.current.canActivateUser(selectedUser.userAcl) : false)
                        .build(),
                    GridAction.buttonBuilder(ButtonId.DEACTIVATE_USER, ActionButtonType.IMMEDIATE, _transl(UsersTranslationKey.DEACTIVATE_USER),
                        <BlockIcon/>)
                        .enabledPolicy(EnabledPolicy.WHEN_EXACTLY_ONE_SELECTED)
                        .onClick(openDeactivationDialog)
                        .isEnabled(() => selectedUser ? controller.current.canDeactivateUser(selectedUser.userAcl) : false)
                        .build(),
                    GridAction.buttonBuilder(ButtonId.CHANGE_PASSWORD, ActionButtonType.IMMEDIATE, _transl(UsersTranslationKey.CHANGE_PASSWORD),
                        <Pin/>)
                        .enabledPolicy(EnabledPolicy.WHEN_EXACTLY_ONE_SELECTED)
                        .onClick(() => setChangeUserPasswordDialogIsOpen(true))
                        .isEnabled(() => selectedUser ? controller.current.canChangePassword(selectedUser.userAcl) : false)
                        .build(),
                    GridAction.buttonBuilder(ButtonId.EXPORT_PERMISSIONS, ActionButtonType.SPEED, _transl(UsersTranslationKey.EXPORT_PERMISSIONS),
                        <StackedIcon icon={<SecurityIcon/>}
                                     secondaryIcon={<ReplyIcon style={{transform: "scaleX(-1)"}}/>}
                                     secondaryIconPosition={Position.TOP_RIGHT_MINIFIED}/>)
                        .enabledPolicy(EnabledPolicy.ALWAYS)
                        .onClick(exportPermissions)
                        .isEnabled(() => controller.current.canExportPermissions(loggedUser.userAcl))
                        .build(),
                    GridAction.buttonBuilder(ButtonId.IMPORT_PERMISSIONS, ActionButtonType.SPEED, _transl(UsersTranslationKey.IMPORT_PERMISSIONS),
                        <StackedIcon icon={<SecurityIcon/>}
                                     secondaryIcon={<ReplyIcon
                                         style={{transform: "rotate(-90deg)"}}/>}
                                     secondaryIconPosition={Position.TOP_RIGHT_MINIFIED}/>)
                        .enabledPolicy(EnabledPolicy.ALWAYS)
                        .onClick(() => setImportDialog({configurationId: ConfigurationId.PERMISSIONS}))
                        .isEnabled(() => controller.current.canImportPermissions(loggedUser.userAcl))
                        .build(),
                ]}
                onRowClick={(param, event) => onGridRowClicked(param)}
                onRowDoubleClick={() => {
                    setUserDetailDialogIsOpen(true);
                }}
                peristentStateId={PersistentStateId.USERS_PAGE_GRID}
                resourceId={""}
            />
        </React.Fragment>
    );
}
